/**
 * SupplierPicker
 *
 * Searchable dropdown of onboarded suppliers used wherever the admin needs to
 * pair a supplier to a workflow (Add Quote, Add Supplier Quote, WOs, etc.).
 * Picking one auto-fills the vendor_* fields at the call-site (name, email,
 * phone, company) and stores `vendor_id` so we can later link back to the
 * supplier's record for performance metrics and compliance checks.
 *
 * Leaving it blank keeps manual entry behaviour intact for off-roster
 * contractors — the caller decides whether to also render an "Add as new
 * supplier record" toggle for auto-creating a roster entry on save.
 *
 * Props:
 *   suppliers  — array of supplier records ({ id, name, contact_name, email,
 *                phone, company_name, trade_categories: [...] })
 *   selectedId — currently-picked supplier id ("" when nothing picked)
 *   onPick     — (supplier | null) => void — pass null to clear the selection
 *   label      — optional override for the input label
 *   testIdPrefix — override for the data-testid prefix (default "quote-supplier")
 */
import { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function SupplierPicker({
  suppliers,
  selectedId,
  onPick,
  label,
  testIdPrefix = "quote-supplier",
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const list = Array.isArray(suppliers)
    ? [...suppliers].sort((a, b) =>
        (a.company_name || a.name || a.contact_name || "").localeCompare(
          b.company_name || b.name || b.contact_name || "", undefined, { sensitivity: "base" }))
    : [];
  const selected = selectedId ? list.find((s) => s.id === selectedId) : null;
  const term = q.trim().toLowerCase();
  const tokens = term.split(/\s+/).filter(Boolean);
  const matches = tokens.length
    ? list.filter((s) => {
        const hay = [
          s.name, s.contact_name, s.email, s.phone, s.company_name, s.trade,
          s.postcode, s.town_city, s.county, s.address, s.coverage_area,
          Array.isArray(s.trade_categories) ? s.trade_categories.join(" ") : s.trade_categories,
          Array.isArray(s.sectors) ? s.sectors.join(" ") : s.sectors,
          Array.isArray(s.areas_covered) ? s.areas_covered.join(" ") : s.areas_covered,
        ].filter(Boolean).join(" ").toLowerCase();
        return tokens.every((t) => hay.includes(t));
      }).slice(0, 30)
    : list.slice(0, 30);

  if (selected) {
    return (
      <div
        className="border border-primary/40 bg-primary/5 rounded-md p-2.5 flex items-start justify-between gap-2"
        data-testid={`${testIdPrefix}-picked`}
      >
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-0.5">Linked Supplier</p>
          <p className="text-sm font-semibold truncate" title={selected.company_name || selected.name}>{selected.company_name || selected.name || selected.contact_name}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {[selected.contact_name, selected.email, selected.phone].filter(Boolean).join(" · ")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => { onPick(null); setQ(""); setOpen(false); }}
          className="text-xs text-muted-foreground hover:text-red-600 px-2 py-1 rounded hover:bg-muted shrink-0"
          data-testid={`${testIdPrefix}-unpick`}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <Label className="text-xs">
        {label || <>Pick an onboarded supplier <span className="text-muted-foreground font-normal">(optional — fills the details below)</span></>}
      </Label>
      <div className="relative mt-1">
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={list.length ? "Search name, contact, email, trade…" : "No suppliers onboarded yet — fill in the fields below"}
          disabled={!list.length}
          data-testid={`${testIdPrefix}-search`}
        />
        {open && list.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-xl max-h-56 overflow-y-auto">
            {matches.length === 0 ? (
              <p className="text-xs text-muted-foreground italic px-3 py-2">No matches — keep typing or fill the form below manually.</p>
            ) : (
              matches.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); onPick(s); setQ(""); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b border-border last:border-0"
                  data-testid={`${testIdPrefix}-option-${s.id}`}
                >
                  <p className="text-sm font-medium truncate">{s.company_name || s.name || s.contact_name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {[s.contact_name, s.email, s.trade || (s.trade_categories || [])[0]].filter(Boolean).join(" · ")}
                  </p>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
